import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Checkbox, Chip, Divider, FormControlLabel, Radio, RadioGroup, Typography, makeStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DescriptionIcon from '@material-ui/icons/Description';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { FormattedMessage, useIntl } from 'react-intl';

import { Modal } from '../../molecules/Modal';
import {
  useCreateExportRequest,
  useDownloadExportRequest,
  EXPORT_REQUEST_STATUS,
  EXPORT_CONTENT,
  MAXIMUM_EXPORT_SIZE_EXCEEDED,
  SUMMARY_LEVEL,
  readExportRequestError
} from '../../../queries/queryExportRequests';
import { useGetFormularies } from '../../../queries/queryInspection';
import { useGetProjects } from '../../../queries/queryProjects';
import { STATUS_CHIP_COLORS, statusKeyFromPublished } from '../../../containers/Inspections/inspectionStatus';
import { parseStringDateToFormat, SIMPLE_DATE_FORMAT } from '../../../utils/utils';
import { useDownloadCenter } from '../../header/DownloadCenterContext';
import styles from './ExportInspectionsModal.styles';

const useStyles = makeStyles(styles);

// "form" is the options step. "completed"/"processing" mirror the API response as-is — per the ticket, the modal's
// content after submitting depends only on the status returned by the API, with no frontend timers or thresholds.
const STEP = {
  FORM: 'form',
  COMPLETED: 'completed',
  PROCESSING: 'processing',
  ERROR: 'error'
};

const buildPeriodLabel = inspectedAtRange => {
  if (!inspectedAtRange || !inspectedAtRange.startDate || !inspectedAtRange.endDate) return null;

  const start = parseStringDateToFormat(inspectedAtRange.startDate, null, SIMPLE_DATE_FORMAT);
  const end = parseStringDateToFormat(inspectedAtRange.endDate, null, SIMPLE_DATE_FORMAT);

  if (!start || !end) return null;
  if (start === end) return start;

  return `${start} - ${end}`;
};

export const ExportInspectionsModal = ({
  open,
  onClose,
  clientId,
  totalCount,
  projectIds,
  formularyIds,
  inspectedAtRange,
  published,
  type,
  query
}) => {
  const classes = useStyles();
  const { messages: intlMessages } = useIntl();
  const { openDownloadCenter } = useDownloadCenter();
  const [step, setStep] = useState(STEP.FORM);
  const [completedExportRequest, setCompletedExportRequest] = useState(null);
  const [sizeError, setSizeError] = useState(null);
  const [content, setContent] = useState(EXPORT_CONTENT.GENERAL);
  const [includeEvidenceCounts, setIncludeEvidenceCounts] = useState(true);
  const [includeAnalyticsSheet, setIncludeAnalyticsSheet] = useState(false);
  const [includeEvidenceDetails, setIncludeEvidenceDetails] = useState(false);
  const [summaryLevel, setSummaryLevel] = useState(SUMMARY_LEVEL.BASIC);

  const selectedFormularyIds = formularyIds || [];
  const isSingleFormulary = selectedFormularyIds.length === 1;

  const isFormularyResponsesEnabled = isSingleFormulary;
  const isFormularyResponsesSelected = content === EXPORT_CONTENT.FORMULARY_RESPONSES;

  useEffect(() => {
    if (!open) return;

    setContent(isSingleFormulary ? EXPORT_CONTENT.FORMULARY_RESPONSES : EXPORT_CONTENT.GENERAL);
  }, [open, isSingleFormulary]);

  const { data: formularies = [] } = useGetFormularies({ clientId });
  const { data: { projects = [] } = {} } = useGetProjects({ clientId, params: { only_main_attributes: true } }, { enabled: open });

  const formularyName = isSingleFormulary && (formularies.find(formulary => `${formulary.id}` === `${selectedFormularyIds[0]}`) || {}).name;
  const projectName = projectIds && projectIds.length === 1 && (projects.find(project => project.id === projectIds[0]) || {}).name;
  const statusKey = statusKeyFromPublished(published);
  const periodLabel = buildPeriodLabel(inspectedAtRange);
  const typeLabel = type && intlMessages[`project.inspections.type.${type}`];
  const hasAnyFilter = !!(formularyName || projectName || periodLabel || statusKey || typeLabel || query);

  const createExportRequest = useCreateExportRequest({ clientId });
  const downloadExportRequest = useDownloadExportRequest();

  const resetState = () => {
    setStep(STEP.FORM);
    setCompletedExportRequest(null);
    setSizeError(null);
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  const handleSeeDownloads = () => {
    handleClose();
    openDownloadCenter();
  };

  const handleGenerate = () => {
    createExportRequest.mutate(
      {
        resource: 'inspections',
        format: 'xlsx',
        projectIds,
        formularyIds: selectedFormularyIds,
        inspectedAtRange,
        published,
        type,
        query,
        content,
        summaryLevel,
        ...(isFormularyResponsesSelected ? { includeEvidenceCounts, includeAnalyticsSheet, includeEvidenceDetails } : {})
      },
      {
        onSuccess: exportRequest => {
          if (exportRequest.status === EXPORT_REQUEST_STATUS.COMPLETED) {
            setCompletedExportRequest(exportRequest);
            setStep(STEP.COMPLETED);
          } else {
            setStep(STEP.PROCESSING);
          }
        },
        onError: error => {
          const apiError = readExportRequestError(error);

          if (apiError.code === MAXIMUM_EXPORT_SIZE_EXCEEDED) {
            setSizeError(apiError.details || {});
            setStep(STEP.ERROR);
          }
        }
      }
    );
  };

  const handleDownload = () =>
    downloadExportRequest.mutate({
      clientId,
      exportRequestId: completedExportRequest.id,
      filename: completedExportRequest.filename
    });

  const renderSectionTitle = (id, defaultMessage) => (
    <Typography variant="subtitle2" className={classes.sectionTitle}>
      <FormattedMessage id={id} defaultMessage={defaultMessage} />
    </Typography>
  );

  const renderScopeRow = (labelId, defaultMessage, value) => (
    <Box className={classes.scopeRow}>
      <Typography variant="body2" className={classes.scopeRowLabel}>
        <FormattedMessage id={labelId} defaultMessage={defaultMessage} />:
      </Typography>
      {value}
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={classes.card}
      title={<FormattedMessage id="inspections.export.title" defaultMessage="Exportar inspecciones" />}
      subtitle={
        step === STEP.FORM ? (
          <FormattedMessage
            id="inspections.export.description"
            defaultMessage="Configura el contenido del archivo. La exportación utilizará los filtros aplicados actualmente."
          />
        ) : null
      }
    >
      {step === STEP.FORM && (
        <Box>
          <Box className={classes.section}>
            {renderSectionTitle('inspections.export.section.scope', 'Alcance')}
            <Box className={classes.scopeSummary}>
              {totalCount != null && (
                <>
                  <Typography variant="body2" className={classes.scopeCount}>
                    <FormattedMessage
                      id="inspections.export.scope.count"
                      defaultMessage="Se exportarán {count} inspecciones"
                      values={{ count: totalCount }}
                    />
                  </Typography>
                  <Typography variant="caption" className={classes.scopeCountHint}>
                    <FormattedMessage
                      id="inspections.export.scope.countHint"
                      defaultMessage="Basado en los filtros aplicados actualmente."
                    />
                  </Typography>
                </>
              )}

              {formularyName &&
                renderScopeRow(
                  'project.inspections.formulary',
                  'Formulario',
                  <Typography variant="body2" className={classes.scopeRowValue}>
                    {formularyName}
                  </Typography>
                )}
              {projectName &&
                renderScopeRow(
                  'common.project',
                  'Proyecto',
                  <Typography variant="body2" className={classes.scopeRowValue}>
                    {projectName}
                  </Typography>
                )}
              {periodLabel &&
                renderScopeRow(
                  'inspections.export.scope.period',
                  'Periodo',
                  <Typography variant="body2" className={classes.scopeRowValue}>
                    {periodLabel}
                  </Typography>
                )}
              {statusKey &&
                renderScopeRow(
                  'common.status',
                  'Estado',
                  <Chip
                    size="small"
                    label={intlMessages[statusKey]}
                    className={classes.statusChip}
                    style={{ backgroundColor: STATUS_CHIP_COLORS[statusKey] }}
                  />
                )}
              {typeLabel &&
                renderScopeRow(
                  'common.type',
                  'Tipo',
                  <Typography variant="body2" className={classes.scopeRowValue}>
                    {typeLabel}
                  </Typography>
                )}
              {query &&
                renderScopeRow(
                  'projects.search',
                  'Búsqueda',
                  <Typography variant="body2" className={classes.scopeRowValue}>
                    {query}
                  </Typography>
                )}

              {!hasAnyFilter && (
                <Typography variant="body2">
                  <FormattedMessage
                    id="inspections.export.scope.noFilters"
                    defaultMessage="Se exportarán todas las inspecciones disponibles según tus permisos."
                  />
                </Typography>
              )}
            </Box>
          </Box>

          <Divider className={classes.sectionDivider} />

          <Box className={classes.section}>
            {renderSectionTitle('inspections.export.section.content', 'Tipo de contenido')}
            <RadioGroup value={content} onChange={event => setContent(event.target.value)}>
              <FormControlLabel
                className={classes.contentOption}
                value={EXPORT_CONTENT.GENERAL}
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" className={classes.contentOptionTitle}>
                      <FormattedMessage id="inspections.export.content.general" defaultMessage="Listado general de inspecciones" />
                    </Typography>
                    <Typography variant="caption" className={classes.contentOptionHint}>
                      <FormattedMessage
                        id="inspections.export.content.generalHint"
                        defaultMessage="Incluye información general, proyecto, ubicación, inspector, formulario, estado, documentos y evidencias."
                      />
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                className={`${classes.contentOption} ${!isFormularyResponsesEnabled ? classes.contentOption_disabled : ''}`}
                value={EXPORT_CONTENT.FORMULARY_RESPONSES}
                control={<Radio color="primary" />}
                disabled={!isFormularyResponsesEnabled}
                label={
                  <Box>
                    <Typography variant="body2" className={classes.contentOptionTitle}>
                      <FormattedMessage
                        id="inspections.export.content.formularyResponses"
                        defaultMessage="Inspecciones con respuestas del formulario"
                      />
                    </Typography>
                    <Typography variant="caption" className={classes.contentOptionHint}>
                      <FormattedMessage
                        id="inspections.export.content.formularyResponsesHint"
                        defaultMessage="Incluye una fila por inspección y las preguntas del formulario como columnas."
                      />
                    </Typography>
                    {!isFormularyResponsesEnabled && (
                      <Typography variant="caption" className={classes.contentOptionDisabledHint}>
                        <FormattedMessage
                          id="inspections.export.content.formularyResponsesDisabledHint"
                          defaultMessage="Esta opción solo está disponible cuando se selecciona un único formulario."
                        />
                      </Typography>
                    )}
                  </Box>
                }
              />
            </RadioGroup>

            {isFormularyResponsesSelected && (
              <Box className={classes.preferences}>
                <FormControlLabel
                  className={classes.checkboxRow}
                  control={
                    <Checkbox
                      checked={includeEvidenceCounts}
                      onChange={event => setIncludeEvidenceCounts(event.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box className={classes.contentOptionLabel}>
                      <Typography variant="body2">
                        <FormattedMessage
                          id="inspections.export.includeEvidenceCounts"
                          defaultMessage="Incluir conteo de evidencias por pregunta"
                        />
                      </Typography>
                      <Chip
                        size="small"
                        className={classes.defaultBadge}
                        label={<FormattedMessage id="inspections.export.default" defaultMessage="Predeterminado" />}
                      />
                    </Box>
                  }
                />
                <FormControlLabel
                  className={classes.checkboxRow}
                  control={
                    <Checkbox
                      checked={includeAnalyticsSheet}
                      onChange={event => setIncludeAnalyticsSheet(event.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <FormattedMessage
                        id="inspections.export.includeAnalyticsSheets"
                        defaultMessage="Incluir hoja analítica de respuestas"
                      />
                    </Typography>
                  }
                />
                <FormControlLabel
                  className={classes.checkboxRow}
                  control={
                    <Checkbox
                      checked={includeEvidenceDetails}
                      onChange={event => setIncludeEvidenceDetails(event.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <FormattedMessage id="inspections.export.includeEvidenceDetails" defaultMessage="Incluir detalle de evidencias" />
                    </Typography>
                  }
                />
                <Box className={classes.infoBox} marginTop="8px">
                  <InfoOutlinedIcon className={classes.infoBoxIcon} />
                  <Typography variant="caption">
                    <FormattedMessage
                      id="inspections.export.preferencesHelp"
                      defaultMessage="El conteo de evidencias estará activo por defecto. Las respuestas por pregunta forman parte del comportamiento base. Las hojas analíticas son opcionales."
                    />
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Divider className={classes.sectionDivider} />

          <Box className={classes.section}>
            {renderSectionTitle('inspections.export.section.format', 'Formato')}
            <Box className={classes.formatValue}>
              <DescriptionIcon className={classes.formatIcon} />
              <Typography variant="body2">
                <FormattedMessage id="inspections.export.format.xlsx" defaultMessage="Excel (.xlsx)" />
              </Typography>
            </Box>
          </Box>

          <Divider className={classes.sectionDivider} />

          <Box className={classes.section}>
            {renderSectionTitle('inspections.export.section.summary', 'Resumen')}
            <Typography variant="body2">
              <FormattedMessage id="inspections.export.summaryLevel" defaultMessage="Resumen del archivo" />
            </Typography>
            <RadioGroup className={classes.summaryOptions} value={summaryLevel} onChange={event => setSummaryLevel(event.target.value)}>
              <FormControlLabel
                value={SUMMARY_LEVEL.BASIC}
                control={<Radio color="primary" />}
                label={<FormattedMessage id="inspections.export.summaryLevel.basic" defaultMessage="Básico" />}
              />
              <FormControlLabel
                value={SUMMARY_LEVEL.ANALYTICS}
                control={<Radio color="primary" />}
                label={<FormattedMessage id="inspections.export.summaryLevel.analytics" defaultMessage="Analítico" />}
              />
              <FormControlLabel
                value={SUMMARY_LEVEL.NONE}
                control={<Radio color="primary" />}
                label={<FormattedMessage id="inspections.export.summaryLevel.none" defaultMessage="No incluir" />}
              />
            </RadioGroup>
            <Typography variant="caption" className={classes.helpText}>
              <FormattedMessage
                id="inspections.export.summaryLevel.help"
                defaultMessage="El resumen básico incluye información general, filtros aplicados y totales principales. El resumen analítico agrega distribuciones y cortes adicionales."
              />
            </Typography>
          </Box>

          <Divider className={classes.sectionDivider} />

          <Box className={classes.section}>
            {renderSectionTitle('inspections.export.section.information', 'Información')}
            <Box className={classes.infoBox}>
              <InfoOutlinedIcon className={classes.infoBoxIcon} />
              <Typography variant="body2">
                <FormattedMessage
                  id="inspections.export.info"
                  defaultMessage="El archivo se agregará al Centro de Descargas cuando esté listo. Puedes continuar trabajando mientras se genera."
                />
              </Typography>
            </Box>
          </Box>

          <Box className={classes.actions}>
            <Button variant="outlined" onClick={handleClose}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
            </Button>
            <Button variant="contained" onClick={handleGenerate} disabled={createExportRequest.isLoading}>
              <FormattedMessage id="inspections.export.generate" defaultMessage="Generar exportación" />
            </Button>
          </Box>
        </Box>
      )}

      {step === STEP.COMPLETED && (
        <Box className={classes.statusContainer}>
          <CheckCircleIcon className={`${classes.statusIcon} ${classes.statusIcon_completed}`} />
          <Typography variant="h6">
            <FormattedMessage id="inspections.export.status.completed" defaultMessage="Exportación lista" />
          </Typography>
          <Typography variant="body2" className={classes.statusHint}>
            <FormattedMessage id="inspections.export.status.completedHint" defaultMessage="El archivo está disponible para descarga." />
          </Typography>
          <Box className={classes.actions} style={{ justifyContent: 'center' }}>
            <Button variant="contained" onClick={handleDownload}>
              <FormattedMessage id="downloadCenter.download" defaultMessage="Descargar" />
            </Button>
            <Button variant="outlined" onClick={handleSeeDownloads}>
              <FormattedMessage id="inspections.export.seeInDownloads" defaultMessage="Ver en Descargas" />
            </Button>
          </Box>
        </Box>
      )}

      {step === STEP.PROCESSING && (
        <Box className={classes.statusContainer}>
          <HourglassEmptyIcon className={`${classes.statusIcon} ${classes.statusIcon_processing}`} />
          <Typography variant="h6">
            <FormattedMessage id="inspections.export.status.processing" defaultMessage="Estamos preparando tu archivo" />
          </Typography>
          <Typography variant="body2" className={classes.statusHint}>
            <FormattedMessage
              id="inspections.export.status.processingHint"
              defaultMessage="Puedes continuar trabajando mientras finaliza la exportación."
            />
          </Typography>
          <Box className={classes.actions} style={{ justifyContent: 'center' }}>
            <Button variant="contained" onClick={handleSeeDownloads}>
              <FormattedMessage id="inspections.export.seeDownloads" defaultMessage="Ver Descargas" />
            </Button>
            <Button variant="outlined" onClick={handleClose}>
              <FormattedMessage id="common.close" defaultMessage="Cerrar" />
            </Button>
          </Box>
        </Box>
      )}

      {step === STEP.ERROR && (
        <Box className={classes.statusContainer}>
          <ErrorOutlineIcon className={`${classes.statusIcon} ${classes.statusIcon_error}`} />
          <Typography variant="h6">
            <FormattedMessage id="inspections.export.error.maxSizeTitle" defaultMessage="No es posible generar la exportación" />
          </Typography>
          <Typography variant="body2" className={classes.statusHint}>
            <FormattedMessage
              id="inspections.export.error.maxSizeHint"
              defaultMessage="La solicitud incluye {recordCount} registros, el máximo permitido es {maximumRecords}. Aplica más filtros para reducir el alcance e intenta nuevamente."
              values={{
                recordCount: (sizeError || {}).recordCount,
                maximumRecords: (sizeError || {}).maximumRecords
              }}
            />
          </Typography>
          <Box className={classes.actions} style={{ justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setStep(STEP.FORM)}>
              <FormattedMessage id="system.actions.back" defaultMessage="Volver" />
            </Button>
            <Button variant="outlined" onClick={handleClose}>
              <FormattedMessage id="common.close" defaultMessage="Cerrar" />
            </Button>
          </Box>
        </Box>
      )}
    </Modal>
  );
};

ExportInspectionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
  totalCount: PropTypes.number,
  projectIds: PropTypes.arrayOf(PropTypes.number),
  formularyIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  inspectedAtRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  published: PropTypes.bool,
  type: PropTypes.string,
  query: PropTypes.string
};
